import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Search, Sparkles, Heart, Trophy, Book } from 'lucide-react';
import clsx from 'clsx';
import { motion } from 'framer-motion';

interface NavItem {
  path: string;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  isCenter?: boolean;
}

const navItems: NavItem[] = [
  { path: '/verses', icon: Search, label: 'Verses' },
  { path: '/daily-motivation', icon: Sparkles, label: 'Daily' },
  { path: '/prayer-room', icon: Heart, label: 'Pray', isCenter: true },
  { path: '/trivia', icon: Trophy, label: 'Trivia' },
  { path: '/bible', icon: Book, label: 'Bible' }
];

export function MobileBottomNav() {
  const navigate = useNavigate();
  const location = useLocation();

  const handleNavigation = (path: string) => {
    navigate(path);
  };

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50 bg-white/95 dark:bg-gray-900/95 backdrop-blur-lg border-t border-gray-200 dark:border-gray-800 md:hidden">
      <div className="relative flex items-center justify-around px-2 pt-2 pb-2">
        {navItems.map((item) => {
          const isActive = location.pathname === item.path;
          const Icon = item.icon;

          if (item.isCenter) {
            return (
              <motion.button
                key={item.path}
                onClick={() => handleNavigation(item.path)}
                className="relative flex flex-col items-center justify-center -mt-6"
                whileTap={{ scale: 0.9 }}
              >
                <motion.div
                  className={clsx(
                    "w-16 h-16 rounded-2xl flex items-center justify-center",
                    "bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800",
                    "shadow-lg shadow-blue-500/50"
                  )}
                  whileHover={{ scale: 1.05 }}
                  animate={{
                    boxShadow: isActive
                      ? '0 20px 25px -5px rgba(59, 130, 246, 0.5), 0 10px 10px -5px rgba(59, 130, 246, 0.4)'
                      : '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
                  }}
                >
                  <Icon className="w-7 h-7 text-white" strokeWidth={2.5} />
                </motion.div>
                <span className="text-xs font-medium text-gray-700 dark:text-gray-300 mt-1">
                  {item.label}
                </span>
              </motion.button>
            );
          }

          return (
            <motion.button
              key={item.path}
              onClick={() => handleNavigation(item.path)}
              className="flex flex-col items-center justify-center min-w-[60px] py-2 px-3"
              whileTap={{ scale: 0.9 }}
            >
              <motion.div
                animate={{
                  scale: isActive ? 1.1 : 1,
                  y: isActive ? -2 : 0
                }}
                transition={{ type: 'spring', stiffness: 400, damping: 17 }}
              >
                <Icon
                  className={clsx(
                    "w-6 h-6 mb-1 transition-colors",
                    isActive
                      ? "text-blue-600 dark:text-blue-500"
                      : "text-gray-500 dark:text-gray-400"
                  )}
                  strokeWidth={isActive ? 2.5 : 2}
                />
              </motion.div>
              <span
                className={clsx(
                  "text-xs font-medium transition-all",
                  isActive
                    ? "text-blue-600 dark:text-blue-500 font-semibold"
                    : "text-gray-600 dark:text-gray-400"
                )}
              >
                {item.label}
              </span>
              {isActive && (
                <motion.div
                  layoutId="activeIndicator"
                  className="absolute -bottom-0 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-blue-600 dark:bg-blue-500 rounded-full"
                  transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                />
              )}
            </motion.button>
          );
        })}
      </div>
      <div className="safe-area-bottom" />
    </nav>
  );
}
